'use client';
import React from 'react';

interface ButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  fullWidth?: boolean;
  loading?: boolean;
}

const Button: React.FC<ButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>> = ({ 
  children, 
  onClick, 
  variant = 'primary', 
  size = 'md', 
  disabled = false,
  type = 'button',
  className = '',
  fullWidth = false,
  loading = false,
  ...props
}) => {
  const variants = {
    primary: 'bg-button-2 text-button-2 hover:bg-[#15b347] active:bg-[#13a03f] focus:ring-green-500',
    secondary: 'bg-button-1 text-global-5 hover:bg-[#00000050] active:bg-[#00000066] focus:ring-gray-300',
    outline: 'border border-gray-300 text-gray-700 bg-transparent hover:bg-gray-50 active:bg-gray-100 focus:ring-gray-300',
    ghost: 'text-gray-700 bg-transparent hover:bg-gray-100 active:bg-gray-200 focus:ring-gray-300',
    danger: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800 focus:ring-red-500'
  };

  // Mobile-first responsive sizes
  const sizes = {
    xs: 'px-2 py-1 text-xs sm:px-2.5 sm:py-1.5 sm:text-xs',
    sm: 'px-2.5 py-1.5 text-xs sm:px-3 sm:py-2 sm:text-sm md:text-sm',
    md: 'px-3 py-2 text-sm sm:px-4 sm:py-2.5 sm:text-base md:text-base',
    lg: 'px-4 py-2.5 text-base sm:px-5 sm:py-3 sm:text-lg md:text-lg lg:text-xl',
    xl: 'px-5 py-3 text-lg sm:px-6 sm:py-4 sm:text-xl md:text-xl lg:text-2xl'
  };

  // Responsive border radius
  const responsiveRadius = 'rounded-[60px] sm:rounded-[75px] md:rounded-[80px]';

  // Responsive focus ring
  const responsiveFocus = 'focus:ring-2 sm:focus:ring-2 md:focus:ring-4';

  // Loading spinner component
  const LoadingSpinner = () => (
    <svg data-component-id="src/components/ui/Button.tsx:53:4" data-component-path="src/components/ui/Button.tsx" data-component-line="53" data-component-end-line="72" data-component-file="Button.tsx" data-component-name="svg" data-component-content="%7B%22elementName%22%3A%22svg%22%2C%22className%22%3A%22animate-spin%20h-4%20w-full%20sm%3Ah-5%20sm%3Aw-5%20mr-2%20sm%3Amr-3%22%7D" 
      className="animate-spin h-4 w-full sm:h-5 sm:w-5 mr-2 sm:mr-3" 
      xmlns="http://www.w3.org/2000/svg" 
      fill="none" 
      viewBox="0 0 24 24"
    >
      <circle data-component-id="src/components/ui/Button.tsx:59:6" data-component-path="src/components/ui/Button.tsx" data-component-line="59" data-component-end-line="66" data-component-file="Button.tsx" data-component-name="circle" data-component-content="%7B%22elementName%22%3A%22circle%22%2C%22className%22%3A%22opacity-25%22%7D" 
        className="opacity-25" 
        cx="12" 
        cy="12" 
        r="10" 
        stroke="currentColor" 
        strokeWidth="4"
      />
      <path data-component-id="src/components/ui/Button.tsx:67:6" data-component-path="src/components/ui/Button.tsx" data-component-line="67" data-component-end-line="71" data-component-file="Button.tsx" data-component-name="path" data-component-content="%7B%22elementName%22%3A%22path%22%2C%22className%22%3A%22opacity-75%22%7D" 
        className="opacity-75" 
        fill="currentColor" 
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  return (
    <button data-component-id="src/components/ui/Button.tsx:76:4" data-component-path="src/components/ui/Button.tsx" data-component-line="76" data-component-end-line="108" data-component-file="Button.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22%5Bvar%3Atype%5D%22%2C%22%5Bspread%5D%22%3A%22true%22%7D"
      type={type}
      onClick={disabled || loading ? undefined : onClick}
      disabled={disabled || loading}
      className={`
        ${responsiveRadius}
        transition-all 
        duration-200 
        ease-in-out
        focus:outline-none 
        ${responsiveFocus}
        focus:ring-opacity-50
        ${variants[variant]} 
        ${sizes[size]} 
        ${fullWidth ? 'w-full' : ''}
        ${disabled || loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:scale-105 active:scale-95'} 
        ${loading ? 'relative' : ''}
        font-medium
        inline-flex
        items-center
        justify-center
        min-h-[44px] sm:min-h-[48px]
        touch-manipulation
        font-poppins
        ${className}
      `.trim().replace(/\s+/g, ' ')}
      aria-disabled={disabled || loading}
      aria-busy={loading}
      {...props}
    >
      {loading && <LoadingSpinner data-component-id="src/components/ui/Button.tsx:106:18" data-component-path="src/components/ui/Button.tsx" data-component-line="106" data-component-end-line="106" data-component-file="Button.tsx" data-component-name="LoadingSpinner" data-component-content="%7B%22elementName%22%3A%22LoadingSpinner%22%7D" />}
      <span data-component-id="src/components/ui/Button.tsx:107:6" data-component-path="src/components/ui/Button.tsx" data-component-line="107" data-component-end-line="107" data-component-file="Button.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D" className={loading ? 'opacity-75' : ''}>{children}</span>
    </button>
  );
};

export default Button;